import React, { useEffect, useState, useRef} from "react";
import { ToastContainer, toast } from "react-toastify";
import { createVenueOrganizer, getAllCountries } from "../../api";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Console } from "console";
import ImageCropper from "../../components/common/ImageCropper/ImageCropper";
import { useNavigate } from "react-router-dom";

const CreateOrganizer = () => {
  const navigate = useNavigate();   
  const [isLoading, setIsLoading] = useState(false);
  const [countries, setCountries] = useState([]); 
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [profilePic, setSelectedThumbImg] = useState<any>(null);
  const [apiError, setApiError] = useState<string | null>(null);


  const handleProfileImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
          if (!file) return;
    
          if (file.size > 3 * 1024 * 1024) {
            toast.error("File size should be less than 3MB");
            return;
          }
    
          setSelectedFile(file);
          setIsCropperOpen(true); 
  };

  useEffect(() => {
    async function fetchCountries() {
      try {
        const res = await getAllCountries();
        setCountries(res || []);
      } catch (err) {
        toast.error("Failed to load countries");
      }
    }
    fetchCountries();
  }, []);

  const validationSchema = Yup.object({
    organizerName: Yup.string().required("Organizer name is required"),
    organizerEmail: Yup.string().email("Invalid email").required("Email is required"),
    organizerPhone: Yup.string()
  .trim()
  .matches(/^\+?[0-9\s\-()]{6,20}$/, {
    message: "Enter a valid phone number",
    excludeEmptyString: true
  }) .required("Phone number is required"),
    country: Yup.string().required("Select a country"),
    password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),

    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password")], "Passwords must match")
      .required("Confirm password is required"),
    profilePic: Yup.mixed()
    .required("Profile image is required")
    .test(
      "fileSize",
      "Image must be less than 3MB",
      (file: any) => !file || file.size <= 3 * 1024 * 1024
    )
    .test(
      "fileType",
      "Only JPG or PNG files are allowed",
      (file: any) =>
        !file || ["image/jpeg", "image/png"].includes(file.type)
    ),

  });

  const formik = useFormik({
    initialValues: {
      organizerName: "",
      organizerEmail: "",
      organizerPhone: "",
      country: "",
      referenceUrl: "",
      password: "",
      confirmPassword: "",
      profilePic: null,
    },
    validationSchema:validationSchema ,
    onSubmit: async (values) => {
      if (isLoading) return;
      setIsLoading(true);
      try{
      const venueData = localStorage.getItem("venueData");
      if (!venueData) return console.log('venueData not found');
      const venueId = JSON.parse(venueData).userId;
      console.log('venueId :',venueId)
      const payload = new FormData();
      payload.append("venueUserId",venueId);
      payload.append("name", values.organizerName);
      payload.append("email", values.organizerEmail);
      payload.append("username", values.organizerEmail);
      payload.append("phoneNo", values.organizerPhone);
      payload.append("url", values.referenceUrl);
      payload.append("location", values.country);
      payload.append("password", values.password);
      payload.append("userRole",'2');
      if (profilePic) payload.append("profileImage", profilePic);
      try {
        await createVenueOrganizer(payload);
        toast.success("Organizer Created Successfully!");
        formik.resetForm();
        setSelectedThumbImg(null);
        navigate("/home", { replace: true });
      } catch (err: any) {
        const message =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Failed to create organizer";

        setApiError(message);
        toast.error(message);
      }
    }finally{
        setIsLoading(false);
      }
    },
  });
  return (
    <div className="col-md-12">
      <div className="event-dt-block p-10">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-12 col-md-12">
              <div className="main-title text-center">
                <h3>Create Organizer</h3>
              </div>
            </div>
            <div className="col-xl-8 col-lg-9 col-md-12">
              <div className="wizard-steps-block">
                <div id="add-event-tab" className="step-app">
                  <div className="step-tab-panel step-tab-info active">
                    <div className="main-card">
                      <div className="bp-title">
                        <h4>
                          <i className="fa-solid fa-circle-info step_icon me-3"></i>
                          Details
                        </h4>
                      </div>
                      <div className="p-2 bp-form main-form">
                        <form onSubmit={formik.handleSubmit}>
                          <div className="row">
                            <div className="col-lg-12 col-md-12">

                              {/* Organizer Name */}
                              <div className="form-group border_bottom pb_30">
                                <label className="form-label fs-16">Give your organizer a name.*</label>
                                <input
                                  className="form-control h_50"
                                  type="text"
                                  placeholder="Enter organizer name here"
                                  name="organizerName"
                                  value={formik.values.organizerName}
                                  onChange={formik.handleChange}
                                />
                                {formik.touched.organizerName && formik.errors.organizerName && <div className="text-danger">{formik.errors.organizerName}</div>}
                              </div>

                              {/* Email */}
                              <div className="form-group border_bottom pb_30 pt_30">
                                <label className="form-label fs-16">Give your organizer an email.*</label>
                                <input
                                  className="form-control h_50"
                                  type="email"
                                  placeholder="Enter organizer email here"
                                  name="organizerEmail"
                                  value={formik.values.organizerEmail}
                                  onChange={formik.handleChange}
                                />
                                {formik.touched.organizerEmail && formik.errors.organizerEmail && <div className="text-danger">{formik.errors.organizerEmail}</div>}
                              </div>

                              {/* Phone */}
                              <div className="form-group border_bottom pb_30 pt_30">
                                <label className="form-label fs-16">Give your organizer a phone number.*</label>
                                <input
                                  className="form-control h_50"
                                  type="tel"
                                  placeholder="Enter organizer phone number here"
                                  name="organizerPhone"
                                  value={formik.values.organizerPhone}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                />
                                { formik.touched.organizerPhone && formik.errors.organizerPhone && <div className="text-danger">{formik.errors.organizerPhone}</div>}
                              </div>

                              {/* Country */}
                              <div className="form-group border_bottom pb_30 pt_30">
                                <label className="form-label fs-16">Select Country*</label>
                                <select
                                  name="country"
                                  className="form-control"
                                  value={formik.values.country}
                                  onChange={formik.handleChange}
                                >
                                  <option value="">Select Country</option>
                                  {countries.map((c:any) => (
                                    <option key={c.countryId} value={c.countryId}>
                                      {c.countryName}
                                    </option>
                                  ))}
                                </select>
                                {formik.touched.country && formik.errors.country && <div className="text-danger">{formik.errors.country}</div>}
                              </div>

                              {/* URL */}
                              <div className="form-group border_bottom pb_30 pt_30">
                                <label className="form-label fs-16">Web/Social Media reference URL</label>
                                <input
                                  className="form-control h_50"
                                  type="url"
                                  placeholder="Enter URL here"
                                  name="referenceUrl"
                                  value={formik.values.referenceUrl}
                                  onChange={formik.handleChange}
                                />
                              </div>

                              {/* Image Upload */}
                              <div className="form-group pt_30 pb_30">
                                <label className="form-label fs-16">
                                  Add profile image
                                </label>
                                <p className="mt-2 fs-14 d-block mb-3 pe_right">
                                  Upload a clear profile image.
                                </p>
                                <div className="content-holder mt-4">
                                  <div className="default-event-thumb">
                                    <div
                                      className="default-event-thumb-btn"
                                      onClick={() => fileInputRef.current?.click()}
                                    >
                                      <div className="thumb-change-btn">
                                        <input
                                          type="file"
                                          ref={fileInputRef}
                                          style={{ display: "none" }}
                                          accept="image/jpeg, image/png"
                                          onChange={handleProfileImageUpload}
                                        />
                                        <label style={{ cursor: "pointer" }}>
                                          Select Image
                                        </label>
                                      </div>
                                    </div>
                                    {profilePic && (
                                      <img
                                        src={URL.createObjectURL(profilePic)}
                                        alt="Profile Preview"
                                        style={{
                                          maxWidth: "100%",
                                          maxHeight: "200px",
                                          marginTop: "15px",
                                          borderRadius: "8px",
                                        }}
                                      />
                                    )}
                                  </div>
                                </div>
                                {formik.touched.profilePic && formik.errors.profilePic && (
                                  <div className="text-danger">{formik.errors.profilePic}</div>
                                )}
                              </div>
                              {/* === Cropper Modal === */}
                                {isCropperOpen && selectedFile && (
                                  <div
                                    style={{
                                      position: "fixed",
                                      top: 0,
                                      left: 0,
                                      width: "100%",
                                      height: "100%",
                                      backgroundColor: "rgba(0, 0, 0, 0.7)",
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      zIndex: 2000,
                                      overflowY: "auto",
                                      padding: "20px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        backgroundColor: "#fff",
                                        padding: "20px",
                                        borderRadius: "10px",
                                        boxShadow: "0 4px 10px rgba(0,0,0,0.3)",
                                        maxWidth: "600px",     
                                        width: "90%",          
                                        maxHeight: "90vh",     
                                        overflowY: "auto",
                                      }}
                                    >
                                      <ImageCropper
                                        imageFile={selectedFile}
                                        onCropComplete={(croppedFile) => {
                                          formik.setFieldValue("profilePic", croppedFile);
                                          setSelectedThumbImg(croppedFile); 
                                          setIsCropperOpen(false);
                                        }}
                                      />

                                      {/* Button Row */}
                                      <div
                                        style={{
                                          marginTop: "20px",
                                          display: "flex",
                                          justifyContent: "center",
                                          alignItems: "center",
                                          gap: "5px",
                                        }}
                                      >
                                        <button
                                          className="btn btn-outline-danger"
                                          onClick={() => setIsCropperOpen(false)}
                                          style={{
                                            minWidth: "85px",
                                            height: "32px",
                                            borderRadius: "8px",
                                            fontWeight: "500",
                                            fontSize: "14px",
                                          }}
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                )}                              
                              {/* Password */}
                              <div className="form-group border_bottom pb_30 pt_30">
                                <label className="form-label fs-16">Password</label>
                                <div className="loc-group position-relative">
                                  <input
                                    type={showPassword ? "text" : "password"}
                                    className="form-control h_50"
                                    placeholder="Enter your password"
                                    name="password"
                                    value={formik.values.password}
                                    onChange={formik.handleChange}
                                  />
                                  <span
                                    className="pass-show-eye"
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={{ cursor: "pointer" }}
                                  >
                                    <i className={showPassword ? "fas fa-eye" : "fas fa-eye-slash"}></i>
                                  </span>
                                </div>
                                { formik.touched.password && formik.errors.password && (
                                  <div className="text-danger">{formik.errors.password}</div>
                                )}
                              </div>
                              {/* Confirm Password */}
                              <div className="form-group pt_30">
                                <label className="form-label fs-16">Confirm Password</label>
                                <div className="loc-group position-relative">
                                  <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    className="form-control h_50"
                                    placeholder="Confirm your password"
                                    name="confirmPassword"
                                    value={formik.values.confirmPassword}
                                    onChange={formik.handleChange}
                                  />
                                  <span
                                    className="pass-show-eye"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    style={{ cursor: "pointer" }}
                                  >
                                    <i
                                      className={
                                        showConfirmPassword ? "fas fa-eye" : "fas fa-eye-slash"
                                      }
                                    ></i>
                                  </span>
                                </div>

                                { formik.touched.confirmPassword && formik.errors.confirmPassword && (
                                  <div className="text-danger">{formik.errors.confirmPassword}</div>
                                )}
                              </div>
                              {/* Submit */}
                              <div className="col-md-12 pt_30 pb_30">
                                <button className="gradient-button mt-3 w-100 h_50" 
                                type="submit"
                                disabled={isLoading}
                                >
                                  <h5>
                                    {isLoading ? "Creating..." : "Create Organizer"}
                                  </h5>
                                </button>

                                {apiError && (
                                  <div className="text-danger mt-2 text-center fw-semibold">
                                    {apiError}
                                  </div>
                                )}
                              </div>

                              <ToastContainer autoClose={2000} />
                            </div>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOrganizer;
